import { useMemo } from "react";
import Images from "../../utils/Images";

const policyTypeMap = {
  daily_commuter: "daily",
  intercity_travel: "intercity",
  okada_rider: "bikers",
  taxi_and_bolt: "drivers",
};

export default function useInsuranceData(
  selectedKey,
  secondaryTab,
  policies = [],
  advancedFilters = {}
) {
  const allItems = useMemo(() => {
    // Ensure policies is always an array
    if (!policies || !Array.isArray(policies) || policies.length === 0) {
      console.log("useInsuranceData: No policies or empty array");
      return [];
    }

    console.log("useInsuranceData: Processing", policies.length, "policies");
    console.log("useInsuranceData: selectedKey =", selectedKey);

    let filteredPolicies = policies.filter((policy) => {
      // For travel insurance, check if selectedKey is "travel" and policy_type is "travel_insurance"
      if (selectedKey === "travel") {
        const matches = policy.policy_type === "travel_insurance";
        return matches;
      }
      // For life insurance, check policy_type or short_description
      if (selectedKey === "life") {
        return (
          policy.policy_type === "life_insurance" ||
          policy.short_description === "life_insurance"
        );
      }
      // For general insurance, check policy_type or short_description
      if (selectedKey === "general") {
        return (
          policy.policy_type === "general_insurance" ||
          policy.short_description === "general_insurance"
        );
      }
      // For other insurance types, use the mapping
      const mappedType = policyTypeMap[policy.policy_type];
      return mappedType === selectedKey;
    });

    console.log("useInsuranceData: After type filter:", filteredPolicies.length, "policies");

    // For agents, show all policies (approved, pending, and rejected)
    // Rejected policies will be shown but can be visually distinguished if needed
    // No status filtering for agents - they should see all available policies

    // Apply advanced filters if provided
    if (advancedFilters && Object.keys(advancedFilters).length > 0) {
      filteredPolicies = filteredPolicies.filter((policy) => {
        // Premium range filter
        if (advancedFilters.minPremium || advancedFilters.maxPremium) {
          const prices = policy.coverage_durations?.map(
            (duration) => duration.price || duration.premium
          ) || [];
          if (prices.length === 0) return false;
          
          const minPrice = Math.min(...prices);
          const maxPrice = Math.max(...prices);

          if (
            advancedFilters.minPremium &&
            maxPrice < advancedFilters.minPremium
          )
            return false;
          if (
            advancedFilters.maxPremium &&
            minPrice > advancedFilters.maxPremium
          )
            return false;
        }

        // Duration filter
        if (advancedFilters.duration) {
          const durations = policy.coverage_durations?.map((duration) => {
            // Handle new format with display_unit and display_value
            if (duration.display_unit === 'hours' && duration.display_value) {
              return duration.display_value;
            }
            if (duration.display_unit === 'days' && duration.display_value) {
              return duration.display_value * 24; // Convert days to hours
            }
            // Handle old format with days
            if (duration.days) {
              return duration.days * 24; // Convert days to hours
            }
            // Handle hours format
            if (duration.hours) {
              return duration.hours;
            }
            return 0;
          }) || [];
          
          // Check if any duration matches the filter
          // Filter duration is in hours or days, need to handle both
          const filterDuration = advancedFilters.duration;
          const hasMatchingDuration = durations.some((dur) => {
            // Check exact match or within range
            return dur === filterDuration || 
                   (filterDuration >= 24 && dur >= filterDuration - 1 && dur <= filterDuration + 1) ||
                   (filterDuration < 24 && Math.abs(dur - filterDuration) <= 1);
          });
          
          if (!hasMatchingDuration) return false;
        }

        // Age range filter (based on eligibility criteria)
        if (advancedFilters.ageRange && policy.eligibility_criteria) {
          const eligibilityText = Array.isArray(policy.eligibility_criteria)
            ? policy.eligibility_criteria.join(" ").toLowerCase()
            : String(policy.eligibility_criteria).toLowerCase();
          
          const ageRange = advancedFilters.ageRange;
          
          // Check if eligibility criteria mentions the age range
          const ageMatches = {
            "18-25": eligibilityText.includes("18") || eligibilityText.includes("25"),
            "26-40": eligibilityText.includes("26") || eligibilityText.includes("40") || 
                     (eligibilityText.includes("25") && eligibilityText.includes("40")),
            "41-60": eligibilityText.includes("41") || eligibilityText.includes("60") ||
                     (eligibilityText.includes("40") && eligibilityText.includes("60"))
          };
          
          if (!ageMatches[ageRange]) return false;
        }

        // Coverage type filter (based on insurance coverage list)
        if (advancedFilters.coverageType && policy.insurance_coverage_list) {
          const coverageTypes = Array.isArray(policy.insurance_coverage_list)
            ? policy.insurance_coverage_list.map((coverage) => 
                (coverage.title || coverage.name || String(coverage)).toLowerCase()
              )
            : [];
          
          const searchType = advancedFilters.coverageType.toLowerCase();
          const hasMatchingCoverage = coverageTypes.some((type) => 
            type.includes(searchType) || searchType.includes(type)
          );
          
          if (!hasMatchingCoverage) return false;
        }

        // Claims process time filter
        if (advancedFilters.claimsTime && policy.claim_process_steps) {
          const claimSteps = Array.isArray(policy.claim_process_steps) 
            ? policy.claim_process_steps 
            : [];
          
          if (claimSteps.length === 0) return false;
          
          const firstStep = claimSteps[0];
          const timeframe = (firstStep.timeframe || firstStep.time_frame || "").toLowerCase();

          if (
            advancedFilters.claimsTime === "instant" &&
            !timeframe.includes("24")
          )
            return false;
          if (
            advancedFilters.claimsTime === "1-3days" &&
            !timeframe.includes("32") &&
            !timeframe.includes("3")
          )
            return false;
          if (
            advancedFilters.claimsTime === "up-to-7days" &&
            !timeframe.includes("7")
          )
            return false;
        }

        return true;
      });
    }

    const items = filteredPolicies
      .filter((policy) => policy && policy.coverage_durations && Array.isArray(policy.coverage_durations))
      .map((policy, index) => {
        const prices = policy.coverage_durations.map(
          (duration) => duration.price || duration.premium
        );
        const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
        const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;

        // Extract provider name from insurance_provider object
        let providerName = "SUNU Assurance";
        if (policy.insurance_provider) {
          if (typeof policy.insurance_provider === 'object') {
            providerName = policy.insurance_provider.company_name || policy.insurance_provider.name || "SUNU Assurance";
          } else {
            providerName = policy.insurance_provider;
          }
        }

        return {
          id: policy.slug || `policy-${index}`,
          provider: providerName,
          premiumMin: minPrice,
          premiumMax: maxPrice,
          originalPolicy: policy,
          policyName: policy.policy_name || "Policy",
          policy_type: policy.policy_type,
          description: policy.description,
          shortDescription: policy.short_description || policy.policy_type,
          companyLogo: policy.insurance_provider?.company_logo || Images.logo_colored,
          status: policy.status, // Include status for potential UI indicators
        };
      });

    console.log("useInsuranceData: Final items count:", items.length);
    return items;
  }, [selectedKey, policies, advancedFilters]);

  return allItems;
}

